// Helper render functions for TaskItemBase to separate concerns and reduce file size.
// @ts-check
import { Box, Typography } from '@mui/material';
import TagChip from '@/components/ui/TagChip';
import { planCalendarTaskTitleFontSize } from '@/components/ui/taskVisualTokens';
import { buildTaskRowTrailingSx } from '@/components/ui/buildTaskRowTitleTrailingSx';
import { getTaskProjects } from '@/features/tasks/utils/data/taskTags';
import { TaskMetadata } from '@/features/tasks/components/atoms/TaskMetadata';
export { renderListContent } from './TaskItemListContent';

/**
 * @param {{
 *   isSmallScreen: boolean,
 *   allProjects: string[],
 *   projectDisplayMap: Map<string, string>,
 *   projectColorMap?: Map<string, number>,
 *   allContexts: string[],
 *   contextDisplayMap: Map<string, string>,
 *   hasConflict: boolean,
 *   onRebalance: (id: string) => void,
 *   task: Task,
 *   formattedDate: string|null,
 *   isOrganizeView: boolean,
 *   isMasterView?: boolean,
 *   isExecutionView?: boolean,
 *   isPlanView?: boolean,
 *   isDashboardView?: boolean,
 *   isDashboardSurface?: boolean,
 *   isTodayUpcomingSurface?: boolean,
 *   displayDuration: number,
 *   isRollup: boolean,
 *   isFocused: boolean,
 *   focusStartTime: number|null,
 *   handleDeleteTask: (id: string) => Promise<void>,
 *   view: string,
 *   isSubtaskView?: boolean,
 *   isCompleted?: boolean,
 *   listId?: string|null,
 *   tasksMap?: Map<string, Task>|null
 * }} props
 */
export const getTaskItemMetadata = ({
  isSmallScreen,
  allProjects,
  projectDisplayMap,
  projectColorMap,
  allContexts,
  contextDisplayMap,
  hasConflict,
  onRebalance,
  task,
  formattedDate,
  isOrganizeView,
  isMasterView = false,
  isExecutionView,
  isPlanView,
  isDashboardView = false,
  isDashboardSurface = false,
  isTodayUpcomingSurface = false,
  displayDuration,
  isRollup,
  isFocused,
  focusStartTime,
  isSubtaskView = false,
  isCompleted = false,
  listId = null,
  tasksMap = null,
}) => {
  const metadataContent =
    isSmallScreen && !isExecutionView ? null : (
      <TaskMetadata
        allProjects={allProjects}
        projectDisplayMap={projectDisplayMap}
        projectColorMap={projectColorMap}
        allContexts={allContexts}
        contextDisplayMap={contextDisplayMap}
        hasConflict={hasConflict}
        onRebalance={onRebalance}
        taskId={task.id}
        taskDate={task.date}
        formattedDate={formattedDate}
        isOrganizeView={isOrganizeView}
        isMasterView={isMasterView}
        isExecutionView={isExecutionView}
        isPlanView={isPlanView}
        isDashboardView={isDashboardView}
        isDashboardSurface={isDashboardSurface}
        isTodayUpcomingSurface={isTodayUpcomingSurface}
        displayDuration={displayDuration}
        isRollup={isRollup}
        isFocused={isFocused}
        focusStartTime={focusStartTime}
        estimatedDuration={task.estimatedDuration}
        rescheduleCount={task.rescheduleCount}
        isSubtaskView={isSubtaskView}
        isCompleted={isCompleted}
        listId={listId}
        task={task}
        tasksMap={tasksMap}
      />
    );

  return (
    <Box
      className="task-item__metadata"
      data-testid="task-item__metadata"
      sx={{
        ...buildTaskRowTrailingSx(),
        minHeight: '14px',
        fontSize: '0.75rem',
        color: 'text.secondary',
      }}
    >
      {metadataContent}
    </Box>
  );
};

/**
 * Screen-reader position announcement ("Title, task 3 of 8") — only shown for
 * listbox rows in execution views where the ordinal is meaningful.
 * @param {(key: string, fallback: string, vars?: object) => string} t
 * @param {{ usesListboxRow: boolean, isExecutionView?: boolean, index?: number, task: Task, tasks?: Task[] }} props
 * @returns {string|undefined}
 */
export const getListPositionLabel = (
  t,
  { usesListboxRow, isExecutionView, index, task, tasks },
) => {
  if (!usesListboxRow || !isExecutionView || typeof index !== 'number') {
    return undefined;
  }
  return t('taskitem.listPosition', '{title}, task {current} of {total}', {
    title: task.title,
    current: index + 1,
    total: tasks?.length ?? 0,
  });
};

/**
 * @param {boolean} usesListboxRow
 * @param {boolean} isSelected
 */
export const getRowA11yProps = (usesListboxRow, isSelected) => ({
  role: usesListboxRow ? 'option' : undefined,
  ariaSelected: usesListboxRow ? isSelected : undefined,
});

/**
 * @param {Task} task
 */
export const renderGridContent = (task, projectDisplayMap = new Map()) => {
  const primaryProject = getTaskProjects(task)[0] || null;
  const displayName = primaryProject
    ? (projectDisplayMap.get(primaryProject.toLowerCase()) ?? primaryProject)
    : null;
  return (
    <Box
      className="task-item__content"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        gap: 0.25,
        minWidth: 0,
        width: '100%',
      }}
    >
      <Typography
        component="div"
        className="task-item__title"
        sx={{
          minWidth: 0,
          fontWeight: 500,
          fontSize: planCalendarTaskTitleFontSize,
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
        }}
        title={task.title}
      >
        {task.title}
      </Typography>
      {primaryProject ? (
        <TagChip
          tag={primaryProject}
          label={displayName}
          dense
          data-testid="task-item__grid-project-chip"
        />
      ) : null}
    </Box>
  );
};
